import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, GitCompareArrows, Pin, ArrowRight } from "lucide-react";
import { usePinnedDomains, trackCompareEvent } from "@/lib/pinned";
import { gradientFromDomain } from "@/lib/gradient";

/**
 * Floating "Compare Domains" tray + side-by-side modal.
 *
 * Mounts once at the app level; reads from the shared `usePinnedDomains`
 * hook. Hidden entirely when the shortlist is empty so it never intrudes
 * on the buyer's first visit.
 */
export default function CompareTray() {
  const { pinned, remove, clear, max } = usePinnedDomains();
  const [open, setOpen] = useState(false);

  if (!pinned || pinned.length === 0) return null;

  return (
    <>
      <div
        data-testid="compare-tray"
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 60,
          background: "var(--cd-navy-950)",
          color: "#fff",
          borderRadius: 18,
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          boxShadow: "0 24px 60px rgba(3,20,47,0.32)",
          fontFamily: "var(--cd-font)",
          maxWidth: "min(94vw, 460px)",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {pinned.map((d) => (
            <PinnedThumb key={d.domain} d={d} onRemove={() => remove(d.domain)} />
          ))}
          {Array.from({ length: max - pinned.length }).map((_, i) => (
            <div
              key={`empty-${i}`}
              aria-hidden
              style={{
                width: 36, height: 36, borderRadius: 10,
                border: "1.5px dashed rgba(255,255,255,0.22)",
              }}
            />
          ))}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "-0.01em" }}>
            Shortlist · {pinned.length}/{max}
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
            Compare picks side-by-side
          </div>
        </div>
        <button
          type="button"
          className="cd-btn cd-btn--accent cd-btn--sm"
          onClick={() => {
            trackCompareEvent("compare.open", { count: pinned.length, domains: pinned.map((d) => d.domain) });
            setOpen(true);
          }}
          data-testid="compare-open-btn"
          disabled={pinned.length < 2}
          title={pinned.length < 2 ? "Pin at least 2 domains to compare" : "Compare shortlist"}
          style={pinned.length < 2 ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
        >
          <GitCompareArrows size={14} strokeWidth={2} />
          Compare
        </button>
      </div>

      {open && (
        <CompareModal pinned={pinned} onClose={() => setOpen(false)} onClear={() => { clear(); setOpen(false); toast.success("Shortlist cleared"); }} />
      )}
    </>
  );
}

function PinnedThumb({ d, onRemove }) {
  const g = gradientFromDomain(d.domain);
  const initials = (d.domain.split(".")[0] || "?").slice(0, 2).toUpperCase();
  return (
    <div style={{ position: "relative" }}>
      <Link
        to={`/l/${d.domain}`}
        title={d.domain}
        aria-label={`View ${d.domain}`}
        style={{
          width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center",
          background: `linear-gradient(135deg, ${g.from}, ${g.to})`,
          color: g.ink, fontWeight: 800, fontSize: 12, letterSpacing: "-0.02em",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
        }}
      >
        {initials}
      </Link>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Unpin ${d.domain}`}
        data-testid={`unpin-${d.domain}`}
        style={{
          position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%",
          background: "#fff", color: "var(--cd-navy-950)", border: 0, cursor: "pointer",
          display: "grid", placeItems: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        }}
      >
        <X size={10} strokeWidth={2.5} />
      </button>
    </div>
  );
}

function CompareModal({ pinned, onClose, onClear }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="compare-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(3,20,47,0.55)", backdropFilter: "blur(10px)",
        display: "grid", placeItems: "center", zIndex: 100, padding: 20,
      }}
    >
      <div
        style={{
          background: "var(--cd-white)",
          borderRadius: 22,
          padding: 32,
          maxWidth: 1080,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 32px 80px rgba(3,20,47,0.35)",
          fontFamily: "var(--cd-font)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <span className="cd-eyebrow">Side-by-side</span>
            <h2 className="cd-h2" style={{ marginTop: 8 }}>Compare your shortlist</h2>
            <p className="cd-muted" style={{ marginTop: 4, fontSize: 14 }}>
              {pinned.length} domain{pinned.length === 1 ? "" : "s"} · owned outright, transferable within 24 hours.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close compare view"
            data-testid="compare-close-btn"
            className="cd-btn cd-btn--ghost cd-btn--sm"
          >
            <X size={16} />
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${pinned.length}, 1fr)`,
            gap: 16,
          }}
        >
          {pinned.map((d) => <CompareColumn key={d.domain} d={d} />)}
        </div>

        <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--cd-gray-200)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <button type="button" onClick={onClear} className="cd-btn cd-btn--outline cd-btn--sm" data-testid="compare-clear-btn">
            Clear shortlist
          </button>
          <a href="mailto:hello@casperdomains.com?subject=Multi-domain%20enquiry" className="cd-btn cd-btn--primary cd-btn--sm" data-testid="compare-enquire-btn">
            Enquire about all {pinned.length} <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
}

function CompareColumn({ d }) {
  const g = gradientFromDomain(d.domain);
  const sld = d.domain.split(".")[0];
  const tld = d.domain.split(".").slice(1).join(".");
  const priceLabel = d.price
    ? `${d.currency === "USD" ? "$" : "€"}${Number(d.price).toLocaleString()}`
    : "Make offer";
  // Heuristics — cheap, deterministic, feels smart without being wrong.
  const brand = brandabilityScore(sld);
  const length = sld.length;
  return (
    <article
      data-testid={`compare-col-${d.domain}`}
      style={{
        border: "1px solid var(--cd-gray-200)",
        borderRadius: 18,
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          aspectRatio: "16 / 9",
          borderRadius: 12,
          background: `linear-gradient(135deg, ${g.from}, ${g.to})`,
          display: "grid",
          placeItems: "center",
          color: g.ink,
          fontWeight: 800,
          fontSize: 28,
          letterSpacing: "-0.03em",
          textAlign: "center",
          padding: 12,
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {d.domain}
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: 20, color: "var(--cd-navy-950)", letterSpacing: "-0.02em" }}>
          {sld}
          {tld && <span style={{ color: "var(--cd-gray-500)", fontWeight: 500 }}>.{tld}</span>}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: "var(--cd-success)", marginTop: 6 }}>
          {priceLabel}
        </div>
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", margin: 0, fontSize: 13 }}>
        <dt style={dtStyle}>TLD</dt>
        <dd style={ddStyle}>.{tld || "—"}</dd>
        <dt style={dtStyle}>Length</dt>
        <dd style={ddStyle}>{length} char{length === 1 ? "" : "s"}</dd>
        <dt style={dtStyle}>Brandability</dt>
        <dd style={ddStyle}>
          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: brand.bg, color: brand.fg }}>
            {brand.label} · {brand.score}/10
          </span>
        </dd>
        <dt style={dtStyle}>Availability</dt>
        <dd style={ddStyle}>{d.status === "sold" ? "Sold" : "Available"}</dd>
      </dl>
      <Link
        to={`/l/${d.domain}`}
        className="cd-btn cd-btn--primary cd-btn--sm cd-btn--full"
        data-testid={`compare-view-${d.domain}`}
        style={{ marginTop: "auto" }}
      >
        View listing <ArrowRight size={14} />
      </Link>
    </article>
  );
}

const dtStyle = { color: "var(--cd-gray-500)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 };
const ddStyle = { margin: 0, color: "var(--cd-navy-950)", fontWeight: 500 };

/** Lightweight brandability score:
 *  - shorter names score higher
 *  - fewer vowel clusters / no digits = higher
 *  - hyphens penalized
 *  Deliberately simple — buyers see a heuristic label, not a black box.
 */
function brandabilityScore(sld) {
  const s = (sld || "").toLowerCase();
  let score = 10;
  if (s.length > 8) score -= (s.length - 8);
  if (/\d/.test(s)) score -= 3;
  if (s.includes("-")) score -= 2;
  if (/(.)\1{2,}/.test(s)) score -= 2;
  score = Math.max(1, Math.min(10, score));
  const label = score >= 8 ? "Excellent" : score >= 6 ? "Strong" : score >= 4 ? "OK" : "Weak";
  const bg = score >= 8 ? "var(--cd-cyan-100)" : score >= 6 ? "var(--cd-blue-100)" : score >= 4 ? "#fef3c7" : "#fee2e2";
  const fg = score >= 8 ? "#0e766a" : score >= 6 ? "#1e40af" : score >= 4 ? "#92400e" : "#991b1b";
  return { score, label, bg, fg };
}

/** Standalone Pin button — drop this anywhere (row, lander, etc.) */
export function PinButton({ listing, variant = "icon" }) {
  const { isPinned, toggle, max } = usePinnedDomains();
  const active = isPinned(listing?.domain);
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = toggle(listing);
    if (!r.ok && r.reason === "max_reached") {
      toast.error(`Shortlist is full — max ${max}. Unpin one first.`);
    } else if (r.ok) {
      toast.success(r.action === "pinned" ? "Pinned to compare" : "Unpinned");
    }
  };
  if (variant === "row") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={active ? `Unpin ${listing?.domain}` : `Pin ${listing?.domain} to compare`}
        data-testid={`pin-${listing?.domain}`}
        title={active ? "Unpin from compare" : "Pin to compare"}
        style={{
          width: 30, height: 30, borderRadius: 8,
          display: "grid", placeItems: "center",
          background: active ? "var(--cd-navy-950)" : "transparent",
          color: active ? "#fff" : "var(--cd-gray-500)",
          border: active ? 0 : "1px solid var(--cd-gray-300)",
          cursor: "pointer",
          transition: "all .18s ease",
        }}
      >
        <Pin size={13} strokeWidth={2} fill={active ? "#fff" : "none"} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pin-${listing?.domain}`}
      className={`cd-btn ${active ? "cd-btn--primary" : "cd-btn--outline"} cd-btn--sm`}
    >
      <Pin size={14} strokeWidth={2} fill={active ? "currentColor" : "none"} />
      {active ? "Pinned" : "Pin to compare"}
    </button>
  );
}
